import { Fragment, useEffect, useRef, useState, Dispatch, SetStateAction, useCallback } from "react";
import { Helmet } from "react-helmet-async";
import axios from "axios";
import { ChevronLeft, Clock, Heart, Home, LogOut, Menu, Search, Send, Smile, Trash, Trash2, UserMinus, X, BellOff } from "react-feather";
import { Link, useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Check, CheckCheck, Gamepad2, MessageCircle, Users, Users2 } from "lucide-react";
import { debounce } from "lodash";
import { getImageUser, getImageUserNotFound } from "../global-components/src/utils/auth";
import { clearText } from "../global-components/src/utils/text";
import { getChatListPreview } from "../utils/chat-list-preview";
import { errorMessage } from "../utils/TextManagerTwo";
import { PACOCA_API_URL } from "../global-components/src/utils/vars";
import ConfirmationModal from "./Modals/ConfirmationModal";
import { useAuth, useMessage, useTheme } from "../global-components/src";
import { Chat, MessageChat } from "../types";
import { api } from "../services/api";
import { useTranslation } from "react-i18next";
import InstantDock from "./instants/instant-dock";
import { getChatListCacheKey } from "../utils/chat-list-cache";
import { getSearchHighlightSegments } from "../utils/chat-search-highlight";

interface MessageSearchResult {
    id: number;
    chat_id: number;
    text: string;
    created_at: string;
    user_id: number;
    file_path?: string;
    file_type?: string;
    gif_url?: string;
    user?: { id: number; name: string; user_name: string; img_account: string };
    chat_user?: { id: number; name: string; user_name: string; img_account: string };
}

interface Props{
    onPacocaIAClick?: () => void;
    selectedMessages: number[];
    setSelectedMessages: Dispatch<SetStateAction<number[]>>;
    showConfirmation: boolean;
    setShowConfirmation: Dispatch<SetStateAction<boolean>>;
    intervalRef?: any;
    cleanOpenChat?: () => void;
    selectedChatId?: number;
    members: Chat[] | null;
    setMembers: Dispatch<SetStateAction<Chat[] | null>>;
    /** Called whenever the active tab changes; passes the current tab value ("principal" | "pending"). */
    onNotificationsTypeChange?: (type: string) => void;
}
const parseChatListMessage = (value: unknown): MessageChat | null => {
    if (!value) return null;
    if (typeof value !== "string") return value as MessageChat;
    try {
        return JSON.parse(value) as MessageChat;
    } catch {
        return null;
    }
};

const formatChatTime = (dateStr: string | undefined, tFn: (key: string) => string): string => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return "";
    const now = new Date();
    const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterdayStart = new Date(todayStart.getTime() - 86400000);
    if (date >= todayStart) {
        return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: false });
    } else if (date >= yesterdayStart) {
        return tFn("chat.yesterday");
    } else {
        return date.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit", year: "2-digit" });
    }
};

const ChatList = ({ onPacocaIAClick, selectedMessages, setMembers, members, selectedChatId, setSelectedMessages, cleanOpenChat, setShowConfirmation, showConfirmation, intervalRef, onNotificationsTypeChange }: Props) => {
    const title = window.innerWidth > 850 ? "Pesquisa" : ""
    const [searchParams, setSearchParams] = useSearchParams();
    const [loadingTermo, setLoadingTermo] = useState(false);
    const [loading, setLoading] = useState(false);
    const [termo, setTermo] = useState(searchParams.get("termo") || "");
    const [termoSearch, setTermoSearch] = useState(termo);
    const [loadingMessages, setLoadingMessages] = useState(true);
    const [end, setEnd] = useState(false);
    const [messageResults, setMessageResults] = useState<MessageSearchResult[]>([]);
    const [loadingMessageResults, setLoadingMessageResults] = useState(false);
    const { token, user } = useAuth();
    const { setMessage } = useMessage();
    const [page, setPage] = useState(1); 
    const navigate = useNavigate();
    const location = useLocation();
    const { isDarkMode } = useTheme();
    const [isDropdownVisible, setDropdownVisible] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [instantCollapsed, setInstantCollapsed] = useState(false);
    const instantScrollTopRef = useRef(0);
    const { t } = useTranslation();
    // const [showConfirmation, setShowConfirmation] = useState(false);
    const [password, setPassword] = useState(''); // Adicione um estado para a senha
    const [errorModal, setErrorModalPassword] = useState(''); // Adicione um estado para a senha
    const [idUserToDelete, setIdUserToDelete] = useState(''); // Adicione um estado para a senha
    const closeRef = useRef<HTMLButtonElement | null>(null);
    const [dataNotFound, setDataNotFound] = useState(false); // Adicione um estado para a senha
    const endOfListRef = useRef<HTMLDivElement | null>(null);
    const [notificationsType, setNotificationsType] = useState("principal");
    const [totalContactRequest, setTotalContactRequest] = useState(0);
    // const [selectedMessages, setSelectedMessages] = useState<number[]>([]);
    const longPressTimer = useRef<NodeJS.Timeout | null>(null);
    const isLongPressRef = useRef(false);
    const longPressTime = 500;
    const startPosition = useRef<{ x: number; y: number } | null>(null);
    const isScrollingRef = useRef(false);
    const scrollTimerRef = useRef<NodeJS.Timeout | null>(null);

    const toggleSelectMessage = (id: number) => {
        setSelectedMessages(prev =>
            prev.includes(id) ? prev.filter(mid => mid !== id) : [...prev, id]
        );
    };

    // Cancela o timer de long press em andamento
    const cancelLongPress = () => {
        if (longPressTimer.current) {
            clearTimeout(longPressTimer.current);
            longPressTimer.current = null;
        }
    };

    // Pressionar por 500ms = long press (funciona para usuários E grupos)
    const onPressStart = (id: number, type: "mouse" | "touch", e: any) => {
        if (type === "mouse" && window.innerWidth <= 850) return;
        if (isScrollingRef.current) return;

        isLongPressRef.current = false;
        cancelLongPress();

        const clientX = type === "touch" ? e.touches[0].clientX : e.clientX;
        const clientY = type === "touch" ? e.touches[0].clientY : e.clientY;
        startPosition.current = { x: clientX, y: clientY };

        longPressTimer.current = setTimeout(() => {
            isLongPressRef.current = true;
            toggleSelectMessage(id);
        }, longPressTime);
    };

    // const markChatAsRead = (chatId: number) => {
    //     setMembers(prev => {
    //         if (!prev) return prev;
    //         const updated = prev.map(m => {
    //             if (m.chat_id !== chatId) return m;
    //             return { ...m, last_message_read: null };
    //         });
    //         if (user?.id) {
    //             localStorage.setItem(`chats-${user.id}`, JSON.stringify(updated));
    //         }
    //         return updated;
    //     });
    // };

    const onPressEnd = (id: number, type: "mouse" | "touch", e: any, navigateTo: string) => {
        if (type === "mouse" && window.innerWidth <= 850) return;
        if (isScrollingRef.current) { cancelLongPress(); return; }

        // Checa se moveu demais (scroll)
        if (startPosition.current) {
            const clientX = type === "touch" ? e.changedTouches[0].clientX : e.clientX;
            const clientY = type === "touch" ? e.changedTouches[0].clientY : e.clientY;
            const dx = Math.abs(clientX - startPosition.current.x);
            const dy = Math.abs(clientY - startPosition.current.y);
            if (dx > 15 || dy > 15) { cancelLongPress(); return; }
        }

        const wasLongPress = isLongPressRef.current;
        cancelLongPress();

        if (!wasLongPress) {
            // clique curto
            if (selectedMessages.length > 0) {
                toggleSelectMessage(id);
            } else {
                // markChatAsRead(id);
                navigate(navigateTo, { replace: true });
            }
        }
    };

    const onPressMove = (e: any, type: "mouse" | "touch") => {
        if (!startPosition.current || !longPressTimer.current) return;
        const clientX = type === "touch" ? e.touches[0].clientX : e.clientX;
        const clientY = type === "touch" ? e.touches[0].clientY : e.clientY;
        const dx = Math.abs(clientX - startPosition.current.x);
        const dy = Math.abs(clientY - startPosition.current.y);
        if (dx > 10 || dy > 10) {
            cancelLongPress();
            isScrollingRef.current = true;
            if (scrollTimerRef.current) clearTimeout(scrollTimerRef.current);
            scrollTimerRef.current = setTimeout(() => { isScrollingRef.current = false; }, 200);
        }
    };

    useEffect(() => {
        const handleClickOutside = () => {
            if (isDropdownVisible) {
                closeDropdown();
            }
        };

        window.addEventListener('click', handleClickOutside);
        return () => {
            window.removeEventListener('click', handleClickOutside);
        };
    }, [isDropdownVisible]);

    const closeDropdown = () => {
        setDropdownVisible(false);
    };

    useEffect(() => {
        setLoadingTermo(true);
    }, [termo]); // Um único useEffect para tratar tudo

    const debouncedHandleInput = useCallback(debounce(setTermoSearch, 500), [termoSearch]);

    useEffect(() => {
        loadChats();
        if (termoSearch.trim().length >= 1) {
            searchMessages(termoSearch.trim());
        } else {
            setMessageResults([]);
        }
    }, [termoSearch, page, notificationsType]); // Um único useEffect para tratar tudo

    const searchMessages = async (q: string) => {
        setLoadingMessageResults(true);
        try {
            const response = await api(token).get(`${PACOCA_API_URL}/chats/messages/search`, { params: { q } });
            setMessageResults(response.data.messages ?? []);
        } catch {
            setMessageResults([]);
        } finally {
            setLoadingMessageResults(false);
        }
    };


    const loadChats = async () => {
        setMembers([]);
        setLoading(true);
        const localKey = getChatListCacheKey(user?.id);
        const localChatsRaw = localStorage.getItem(localKey);
        let localChats: any[] = [];

        if (localChatsRaw && notificationsType === 'principal' && !termoSearch.trim().length) {
            try {
                localChats = JSON.parse(localChatsRaw);
                setMembers(localChats);
            } catch (e) {
                console.warn("Erro ao ler chats locais:", e);
            }
        }
        
        const url = new URL(`${PACOCA_API_URL}/chats`);
        url.searchParams.append("search", termoSearch);
        url.searchParams.append("status", notificationsType);

        try {
            const response = await api(token).get(url.href);

            const data = response.data.members;
            // 🔥 Mescla os chats vindos do back com os dados locais
            const mergedChats = data.map((chatFromApi: any) => {
                const local = localChats.find(c => c.chat_id === chatFromApi.chat_id);
                if (local) {
                    const apiLastMessageRaw = chatFromApi.last_message ?? null;
                    const apiLastMessage = parseChatListMessage(apiLastMessageRaw);
                    const localLastMessage = parseChatListMessage(local.last_message);
                    const sameLastMessage = apiLastMessage?.id && localLastMessage?.id
                        && Number(apiLastMessage.id) === Number(localLastMessage.id);
                    const mergedLastMessage = sameLastMessage
                        ? {
                            ...apiLastMessage,
                            ...(apiLastMessage.is_read === undefined && localLastMessage.is_read !== undefined
                                ? { is_read: localLastMessage.is_read, read_at: localLastMessage.read_at }
                                : {}),
                            ...(apiLastMessage.idNotSend === undefined && localLastMessage.idNotSend !== undefined
                                ? { idNotSend: localLastMessage.idNotSend }
                                : {}),
                        }
                        : null;

                    return {
                        ...chatFromApi,
                        last_message_read: local.last_message_read ?? null,
                        has_new_message: local.has_new_message ?? false,
                        // The index endpoint may omit read metadata. Preserve
                        // the receipt learned from the active conversation when
                        // both values refer to the same message.
                        last_message: mergedLastMessage
                            ? typeof apiLastMessageRaw === "string"
                                ? JSON.stringify(mergedLastMessage)
                                : mergedLastMessage
                            : apiLastMessageRaw,
                    };
                }
                return { ...chatFromApi, last_message_read: null, has_new_message: false };
            });

            setTotalContactRequest(response.data.total_contact_request);
            setMembers(mergedChats);
            // Only cache the principal tab — pending/requests must not overwrite the principal cache
            if (notificationsType === 'principal') {
                localStorage.setItem(localKey, JSON.stringify(mergedChats));
            }
            // const data = response.data;
            // setMembers(data.members);
            // localStorage.setItem(`chats-${user?.id}`, JSON.stringify(data.members));
        } catch (err: any) {
            const type = err?.response?.data?.type_message || "error";
            const messageError = errorMessage(err)
            setMessage(messageError, type)
            console.error('Erro:', err);
            console.error('Erro:', messageError);
        } finally {
            setLoading(false);
            setLoadingTermo(false);
        }
    };

    const deleteChat = async () => {
        setShowConfirmation(false);
        setSelectedMessages([]);

        try {
            // percorre todos os IDs selecionados
            if (selectedChatId && selectedMessages.includes(selectedChatId)) {
                if (intervalRef.current) clearInterval(intervalRef.current);
                if(cleanOpenChat) cleanOpenChat()
            }

            for (const chatIdToDelete of selectedMessages) {
                const url = `${PACOCA_API_URL}/chats/${chatIdToDelete}`;

                // Remove mensagens do chat deletado do localStorage
                const stored = localStorage.getItem(`messagesByChat-${user?.id}`);
                if (stored) {
                    const messagesByChat = JSON.parse(stored);
                    delete messagesByChat[chatIdToDelete];
                    localStorage.setItem(`messagesByChat-${user?.id}`, JSON.stringify(messagesByChat));
                }

                // Atualiza a lista de membros no estado e localStorage
                setMembers((prevMembers) => {
                    if (prevMembers) {
                        const filteredMembers = prevMembers.filter(
                            (member) => (member?.chat_id) !== chatIdToDelete
                        );

                        localStorage.setItem(getChatListCacheKey(user?.id), JSON.stringify(filteredMembers));
                        return filteredMembers;
                    }
                    return null;
                });

                // Requisição DELETE para a API
                await axios.delete(url, {
                    headers: {
                        "Content-Type": "application/json",
                        "Accept": "application/json",
                        "Authorization": `Bearer ${token}`,
                    },
                });

                // Remove cache individual do chat
                localStorage.removeItem(getChatListCacheKey(idUserToDelete));
            }

        } catch (err: any) {
            setShowConfirmation(false);
            const messageError = errorMessage(err);
            setMessage(messageError, "error");
            console.error(err);
            console.error(messageError);
        } finally {
            setLoadingMessages(false);
        }
    };

    
    const changeNotificationsType = (type: string) => {
        if(loading) return;
        // Se o tipo for diferente, atualizar o estado
        if (type !== notificationsType) {
            setNotificationsType(type);
            onNotificationsTypeChange?.(type);
        }
    }

    const onClose = () => {
        setPassword("");
        setSelectedMessages([])
        setShowConfirmation(false)
    }

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const inputValue = event.target.value;
        setTermo(inputValue)
        debouncedHandleInput(inputValue)
        setMembers([])
        setMessageResults([])
        setPage(1)
        setEnd(false)
        setSearchParams({ termo: inputValue }, { replace: true })
    };


    return (
        <div className="chat-list-shell">
            <ConfirmationModal
                show={showConfirmation}
                onClose={() => onClose()}
                onConfirm={() => deleteChat()} // Passa a senha aqui
                title={`Apagar conversa${selectedMessages.length > 1 ? "s" : ""}?`}
                coverMobile={false}
                text={t("chat.deleteConversationWarning")}
                requirePassword={false}
                setPassword={setPassword} // Adicione a função setPassword aqui
                errorMessage={errorModal}
                isLoading={isLoading}
                closeRef={closeRef}
            />

            <div className="header-chat-pc">
                <img src={isDarkMode ? "/img/2.png" : "/img/1-1.png"} className="img-logo-chat" alt="" />

                <div className="d-flex container-search-followers container-search-padding" style={{ marginTop: "0px" }}>
                    <input
                        id="input-search-screen"
                        className="form-control input-search input-search-screen input-search-followers"
                        type="text"
                        name="search"
                        placeholder={t("generics.search")}
                        aria-label="Search"
                        required
                        value={termo}
                        onChange={handleChange}
                    />
                    <button className="btn-search btn-search-followers input-search-screen-icon-menu-dark btn" type="button" title="Buscar">
                        <Search color="#d3d3d3" />
                    </button>
                </div>
                <div className="d-flex row row-fillter-search">
                    <button className={`btn col-notification btn-light all ${notificationsType == "principal" ? "notification-type-active" : ""}`} onClick={() => changeNotificationsType("principal")}>
                        <p>{t("chat.main")}</p>
                    </button>
                    <button className={`btn col-notification btn-light mention ${notificationsType == "general" ? "notification-type-active" : ""}`} onClick={() => changeNotificationsType("general")}>
                        <p>{t("chat.general")}</p>
                    </button>
                    <button className={`btn col-notification btn-light mention ${notificationsType == "pending" ? "notification-type-active" : ""}`} onClick={() => changeNotificationsType("pending")}>
                        <p>{t("chat.requests")}</p>
                        {totalContactRequest > 0 ? <span className="badge bg-primary">{totalContactRequest}</span>: null }
                    </button>
                </div>
            </div>

            <div
                className={`content-chat new-list ${!!selectedMessages?.length && "content-chat-with-selects"}`}
                onScroll={event => {
                    const next = event.currentTarget.scrollTop;
                    const previous = instantScrollTopRef.current;
                    if (next > previous + 3 && next > 16) setInstantCollapsed(true);
                    else if (next < previous - 3 || next <= 4) setInstantCollapsed(false);
                    instantScrollTopRef.current = next;
                }}
            >
                {notificationsType == "principal" && ("paçoca ai".toLowerCase().includes(termo) || !termo.length) ? (
                    <div style={{ marginBottom: "5px!important", position: "relative", margin: "0", opacity: selectedMessages.length ? 0.5 : 1 }} className="user-select-none  row-notification list-chat list-chat-light row">
                        <div onClick={() => { if(!selectedMessages.length) navigate(`/chat`, { replace: true }); if(onPacocaIAClick) onPacocaIAClick() }} className="col-img-chat-mobile user-select-none col-1" style={{ display: "flex", alignItems: "center" }}>
                            <img src={`/img/pacoca_ai.png`} className="img-list-chat" />
                        </div>
                        <div onClick={() => { if(!selectedMessages.length) navigate(`/chat`, { replace: true }); if(onPacocaIAClick) onPacocaIAClick() }} id="col-chat-name-mobile" className="col col-chat-name-mobile text-nowrap" style={{ display: "flex", flexDirection: "column", justifyContent: "center", overflow: "hidden" }}>
                            <h5 style={{marginBottom: "0"}} className="title-notification">Paçoca AI</h5>
                            <p>{t("pacocaAI.text3")}</p>
                        </div>
                    </div>
                ) : null}

                {termoSearch.length > 0 ? (
                    <div className="msg-search-section msg-search-section-header" style={{ marginTop: 10, width: "100%" }}>
                        <Users2 size={14} style={{ marginRight: 6, opacity: 0.6 }} />
                        {t("chat.people")}
                    </div>
                ): null}

                {members?.map((member, index) => {
                    const lastMessage = parseChatListMessage(member.last_message);
                    const isGroup = !!member.is_group;
                    const displayName = isGroup ? (member.group_name ?? t("chat.group.panelTitle")) : member.name;
                    const displayImg = isGroup ? member.group_img ?? null : member.img_account;
                    const chatPreview = getChatListPreview(lastMessage, user?.id, isGroup, t, member.name ?? displayName);

                    return (
                        <div
                            onMouseDown={(e) => onPressStart(member.chat_id, "mouse", e)}
                            onMouseUp={(e) => onPressEnd(member.chat_id, "mouse", e, isGroup ? `/chat/${member.chat_id}` : `/chat/@${member.user_name}`)}
                            onMouseMove={(e) => onPressMove(e, "mouse")}
                            onTouchStart={(e) => onPressStart(member.chat_id, "touch", e)}
                            onTouchEnd={(e) => onPressEnd(member.chat_id, "touch", e, isGroup ? `/chat/${member.chat_id}` : `/chat/@${member.user_name}`)}
                            onTouchMove={(e) => onPressMove(e, "touch")}
                            key={index}
                            className={`user-select-none row-notification list-chat list-chat-light row ${selectedMessages.includes(member.chat_id) ? "selected-message user-select-none" : ""}`}
                            style={{ marginBottom: "5px!important", position: "relative", margin: "0" }}
                        >
                            
                            {/* Imagem do usuário / grupo */}
                            <div className="col-img-chat-mobile user-select-none" style={{ display: "flex", alignItems: "center" }}>
                                {isGroup && !displayImg ? (
                                    <div className="img-list-chat group-avatar-placeholder" style={{ display: "flex", alignItems: "center", justifyContent: "center", background: "var(--hover-background,#e0e0e0)", borderRadius: "50%", flexShrink: 0 }}>
                                        <Users size={20} opacity={0.6} />
                                    </div>
                                ) : (
                                    <img src={displayImg ? getImageUser(displayImg) : getImageUser(member?.img_account)} className="img-list-chat" onError={(e) => { e.currentTarget.src = getImageUserNotFound(displayName); }}/>
                                )}
                            </div>

                            {/* Nome e última mensagem */}
                            <div className="col col-chat-name-mobile text-nowrap" id="col-chat-name-mobile"
                                style={{
                                    display: "flex",
                                    flexDirection: "row",
                                    alignItems: "stretch",
                                    overflow: "hidden",
                                    gap: 4,
                                    position: "relative"
                                }}
                            >
                                {/* Texto: nome + prévia */}
                                <div style={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "center", overflow: "hidden", minWidth: 0 }}>
                                    <h5 className="title-notification" style={{ marginBottom: "0", display: "flex", alignItems: "center", gap: 4 }}>
                                        {displayName}
                                        {member.is_muted ? <BellOff size={12} opacity={0.45} style={{ flexShrink: 0, marginTop: 1 }} /> : null}
                                    </h5>

                                    {lastMessage?.id ? (
                                        <p>
                                            {chatPreview.status !== "none" ? (
                                                <span
                                                    className={`chat-list-message-status chat-list-message-status--${chatPreview.status}`}
                                                    aria-label={chatPreview.status === "read" ? "Mensagem visualizada" : chatPreview.status === "delivered" ? "Mensagem recebida" : "Mensagem enviada"}
                                                >
                                                    {chatPreview.status === "sent" ? (
                                                        <Check size={13} strokeWidth={2.2} />
                                                    ) : (
                                                        <CheckCheck size={13} strokeWidth={2.2} />
                                                    )}
                                                </span>
                                            ) : null}
                                            {chatPreview.status !== "none" ? " " : null}
                                            <span
                                                className="text-previview-chat"
                                                dangerouslySetInnerHTML={{
                                                    __html: clearText(chatPreview.text)
                                                }}
                                            />
                                        </p>
                                    ) : (
                                        <p className="text-previview-chat text-muted" style={{ fontStyle: "italic" }}>
                                            {t("empty.message")}
                                        </p>
                                    )}
                                </div>

                                {/* Meta: horário + badge de não lidas */}
                                {lastMessage?.id ? (() => {
                                    const hasUnread = member.last_message_read && Number(lastMessage.user_id) != user?.id && member.last_message_read != lastMessage.id;
                                    return (
                                        <div className="chat-list-meta">
                                            <span className={`chat-list-time${hasUnread ? " chat-list-time--unread" : ""}`}>
                                                {formatChatTime(lastMessage.created_at, t)}
                                            </span>
                                            {hasUnread ? (
                                                <span className="badge-new-message" />
                                            ) : (
                                                <span className="chat-list-meta-spacer" />
                                            )}
                                        </div>
                                    );
                                })() : null}
                            </div>
                        </div>
                    );
                })}

                {(loading || loadingTermo) && !dataNotFound ? (
                    <>
                        {[...Array(1)].map((_, index) => (
                        <div key={index} className="row-notification list-chat list-chat-light row">
                                <span className="user-select-none col-1 img-account-search img-notification placeholder"/>
                                <div className="col" style={{ paddingLeft: "5px", overflow: "hidden",  }}>
                                    <h5 className="h5-name-search name-card-search-user" style={{ overflowWrap: "anywhere", whiteSpace: "nowrap", overflow: "hidden", marginBottom: "2px" }}>
                                        <span className="name-search" style={{ marginRight: 5 }}>
                                        <div className="placeholder" style={{height: "10px", width: "90%"}}></div>
                                        </span>
                                    </h5>
                                    <p className="username-search" style={{ whiteSpace: "nowrap", overflow: "hidden" }}>
                                        <span className="placeholder mt-1" style={{height: "10px", width: "100px"}}></span>
                                    </p>
                                </div>
                            </div>
                        ))}
                    </>
                ) : (
                    <>
                    {(!members || members.length <= 0) ? (
                        <p className="msg-search-empty">{termoSearch ? (notificationsType == "pending" ? t("empty.contactsRequests") : t("empty.chats")) : (notificationsType == "pending" ? t("empty.contactsRequests") : t("empty.startChat"))}</p>
                    ) : null}
                        {/* {(!members || members?.length <=0) && messageResults.length === 0 && (
                            <div className="user-select-none row" style={{ marginTop: "24px", width: "100%", marginLeft: "0", marginRight: "0" }}>
                                <span className="span-empty" style={{width: "100%", marginTop: "250px"}}>
                                    <MessageCircle className="icon-empty" />
                                    <h3 className="w-100 text-center">
                                            {termoSearch ? (notificationsType == "pending" ? t("empty.contactsRequests") : t("empty.chats")) : (notificationsType == "pending" ? t("empty.contactsRequests") : t("empty.startChat"))}
                                    </h3>
                                </span>
                            </div>
                        )} */}
                    </>
                )}
            </div>

            {termoSearch.trim().length >= 1 && (
                <div className="msg-search-section" style={{ marginTop: -90 }}>
                    <div className="msg-search-section-header">
                        <MessageCircle size={14} style={{ marginRight: 6, opacity: 0.6 }} />
                        {t("chat.messagesSection")}
                    </div>

                    {loadingMessageResults || loadingTermo ? (
                        <div className="row-notification list-chat list-chat-light row">
                            <span className="user-select-none col-1 img-account-search img-notification placeholder"/>
                            <div className="col" style={{ paddingLeft: "5px", overflow: "hidden" }}>
                                <div className="placeholder" style={{ height: "10px", width: "80%", marginBottom: 6 }}></div>
                                <div className="placeholder" style={{ height: "10px", width: "60%" }}></div>
                            </div>
                        </div>
                    ) : messageResults.length === 0 ? (
                        <p className="msg-search-empty">{t("empty.chats")}</p>
                    ) : (
                        messageResults.map((msg) => (
                            <div
                                key={msg.id}
                                className="user-select-none row-notification list-chat list-chat-light row msg-search-result"
                                style={{ margin: 0, cursor: "pointer" }}
                                onClick={() => navigate(`/chat/@${msg.chat_user?.user_name}?message=${msg.id}`)}
                            >
                                <div className="col-img-chat-mobile user-select-none" style={{ display: "flex", alignItems: "center" }}>
                                    <img
                                        src={getImageUser(msg.chat_user?.img_account)}
                                        className="img-list-chat"
                                        onError={(e) => { e.currentTarget.src = getImageUserNotFound(msg.chat_user?.name ?? ""); }}
                                    />
                                </div>
                                <div className="col col-chat-name-mobile text-nowrap" style={{ display: "flex", flexDirection: "column", justifyContent: "center", overflow: "hidden" }}>
                                    <h5 className="title-notification" style={{ marginBottom: 0 }}>
                                        {msg.chat_user?.name}
                                    </h5>
                                    <p className="msg-search-snippet">
                                        {getSearchHighlightSegments(msg.text, termoSearch).map((segment, index) => (
                                            segment.isMatch ? (
                                                <mark className="msg-search-highlight" key={`${msg.id}-${index}`}>
                                                    {segment.text}
                                                </mark>
                                            ) : (
                                                <Fragment key={`${msg.id}-${index}`}>{segment.text}</Fragment>
                                            )
                                        ))}
                                    </p>
                                </div>
                            </div>
                        ))
                    )}
                </div>
            )}

            <p ref={endOfListRef}></p>
            {notificationsType === "principal" && !termoSearch.trim() && <InstantDock collapsed={instantCollapsed} reloadWhenEmptyKey={location.key} />}
        </div>
    );
};

export default ChatList;